import React, { useId } from "react";
import "./InputFields.css";

const mergeRefs = (...refs) => (node) => {
  refs.forEach((r) => {
    if (typeof r === "function") r(node);
    else if (r && typeof r === "object") r.current = node;
  });
};

const DollerInput = React.forwardRef(
  (
    {
      name,
      label,
      value,
      placeholder,
      onChange,
      error,
      helperText,
      endAdornment,
      onBlur,
      isRight,
      id,
      handleInputChange,
      handleChange,
      showErrorOnly,
      fieldHeight,
      className,
      readOnly,
      min,
      max,
      style: rootStyle,
      disable,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    const isDisabled = Boolean(disable || disabled);
    const {
      onFocus: propsOnFocus,
      onKeyDown: propsOnKeyDown,
      type,
      ...restProps
    } = props;

    const uid = useId();
    const inputId = id || name || `cs-dollar-${uid}`;

    const handleFocus = (e) => {
      if (isDisabled) return;
      if (value === 0 || value === "0") {
        e.target.select();
      }
      if (propsOnFocus) {
        propsOnFocus(e);
      }
    };

    const handleBlurInternal = (e) => {
      if (isDisabled) {
        if (onBlur) onBlur(e);
        return;
      }
      if (
        e.target.value === "" ||
        e.target.value === undefined ||
        e.target.value === null
      ) {
        if (onChange) {
          onChange({
            target: {
              name: name,
              value: 0,
            },
          });
        }
      }
      if (onBlur) {
        onBlur(e);
      }
    };

    const handleValueChange = (e) => {
      if (isDisabled) return;
      let newValue = e.target.value.replace(/[^0-9.]/g, "");

      if (
        newValue.length > 1 &&
        newValue.startsWith("0") &&
        newValue[1] !== "."
      ) {
        newValue = newValue.replace(/^0+/, "");
      }

      if (newValue === "") {
        newValue = "";
      }

      onChange({
        target: {
          name: name,
          value: newValue,
        },
      });
    };

    const hasErr = Boolean(error);
    // Project-wide rule: never surface inline error text below a field — the
    // red border/shell is the only error affordance. Hint helperText (when
    // there is no error) still renders for fields that need guidance copy.
    const displayHelperText = hasErr || showErrorOnly ? undefined : helperText;
    const height = fieldHeight || "44px";

    /* Same as InputText: label="Amount *" → strip star, red .cs-field-label-required */
    let displayLabel = label;
    let effectiveRequired = Boolean(required);
    if (typeof label === "string") {
      const stripped = label.replace(/\s*\*\s*$/, "");
      if (stripped !== label) {
        displayLabel = stripped;
        effectiveRequired = true;
      }
    }

    const derivedPlaceholder =
      displayLabel
        ? `Enter ${String(displayLabel).replace(/^Enter\s+/i, "").trim()}`
        : undefined;
    const effectivePlaceholder = placeholder ?? derivedPlaceholder;

    const groupClass = [
      "cs-currency-group",
      hasErr ? "cs-currency-group--error" : "",
      isDisabled ? "cs-currency-group--disabled" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={["cs-currency-field", className].filter(Boolean).join(" ")}
        style={rootStyle}
        aria-disabled={isDisabled || undefined}
        aria-required={effectiveRequired ? true : undefined}
      >
        {displayLabel ? (
          <label
            htmlFor={inputId}
            className={`cs-currency-label ${hasErr ? "cs-currency-label--error" : ""} ${isDisabled ? "cs-currency-label--disabled" : ""}`}
          >
            {displayLabel}
            {effectiveRequired ? (
              <span className="cs-field-label-required" aria-hidden="true">
                {" "}
                *
              </span>
            ) : null}
          </label>
        ) : null}
        <div
          className={groupClass}
          style={{
            height,
            minHeight: height,
            maxHeight: height,
            boxSizing: "border-box",
          }}
        >
          <span className="cs-currency-symbol" aria-hidden>
            $
          </span>
          <input
            id={inputId}
            name={name}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={value ?? ""}
            placeholder={effectivePlaceholder}
            disabled={isDisabled}
            readOnly={!isDisabled && readOnly}
            min={min}
            max={max}
            onChange={handleValueChange}
            onFocus={handleFocus}
            onBlur={handleBlurInternal}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
              if (propsOnKeyDown) {
                propsOnKeyDown(e);
              }
            }}
            ref={mergeRefs(ref)}
            className={[
              "cs-currency-input",
              hasErr ? "cs-currency-input--error-text" : "",
              isRight === false ? "cs-currency-input--align-left" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            pattern="^[0-9.]*$"
            {...restProps}
          />
          {endAdornment ? (
            <span className="cs-currency-end">{endAdornment}</span>
          ) : null}
        </div>
        {hasErr && displayHelperText ? (
          <div className="cs-field-helper cs-field-helper--error" role="alert">
            {displayHelperText}
          </div>
        ) : displayHelperText ? (
          <div className="cs-field-helper">{displayHelperText}</div>
        ) : null}
      </div>
    );
  }
);
export default DollerInput;
